import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { Save as SaveIcon, Cancel as CancelIcon } from "@mui/icons-material";

/**
 * Formulario para crear/editar grupos
 *
 * REGLAS APLICADAS:
 * - Solo <Stack> para layout (NO Grid)
 * - Todos los componentes con size="small"
 * - Validación con class-validator en backend
 * - Máximo 500 líneas
 */

interface Group {
  id: string;
  nombre: string;
  descripcion: string;
  activo: boolean;
  es_sistema: boolean;
  fecha_creacion: string;
  fecha_actualizacion: string;
}

interface GroupFormData {
  nombre: string;
  descripcion: string;
  activo: boolean;
}

interface GroupFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (groupData: GroupFormData) => Promise<void>;
  group?: Group | null; // null para crear, Group para editar
  loading?: boolean;
}

export function GroupForm({
  open,
  onClose,
  onSave,
  group,
  loading = false,
}: GroupFormProps) {
  const [formData, setFormData] = useState<GroupFormData>({
    nombre: "",
    descripcion: "",
    activo: true,
  });
  const [errors, setErrors] = useState<Partial<GroupFormData>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = Boolean(group);

  // Cargar datos del grupo cuando se abre el modal para edición
  useEffect(() => {
    if (open) {
      if (isEditing && group) {
        setFormData({
          nombre: group.nombre,
          descripcion: group.descripcion,
          activo: group.activo,
        });
      } else {
        // Resetear formulario para nuevo grupo
        setFormData({
          nombre: "",
          descripcion: "",
          activo: true,
        });
      }
      setErrors({});
      setSubmitError(null);
    }
  }, [open, isEditing, group]);

  const handleInputChange =
    (field: keyof GroupFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value =
        field === "activo" ? event.target.checked : event.target.value;
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      // Limpiar error del campo cuando el usuario empiece a escribir
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: undefined,
        }));
      }
    };

  const validateForm = (): boolean => {
    const newErrors: Partial<GroupFormData> = {};

    // Validar nombre
    if (!formData.nombre.trim()) {
      newErrors.nombre = "El nombre es requerido";
    } else if (formData.nombre.trim().length < 2) {
      newErrors.nombre = "El nombre debe tener al menos 2 caracteres";
    } else if (formData.nombre.trim().length > 100) {
      newErrors.nombre = "El nombre no puede exceder 100 caracteres";
    } else if (!/^[a-zA-Z0-9\s\-_]+$/.test(formData.nombre.trim())) {
      newErrors.nombre =
        "El nombre solo puede contener letras, números, espacios, guiones y guiones bajos";
    }

    // Validar descripción
    if (
      formData.descripcion.trim() &&
      formData.descripcion.trim().length > 500
    ) {
      newErrors.descripcion = "La descripción no puede exceder 500 caracteres";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Preparar datos para envío
      const dataToSend: GroupFormData = {
        nombre: formData.nombre.trim(),
        descripcion: formData.descripcion.trim(),
        activo: formData.activo,
      };

      await onSave(dataToSend);
      handleClose();
    } catch (error) {
      console.error("Error guardando grupo:", error);
      setSubmitError(
        error instanceof Error
          ? error.message
          : "Error desconocido al guardar el grupo",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !isSubmitting) {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isSubmitting}
    >
      <DialogTitle>
        <Typography variant="h6">
          {isEditing ? "Editar Grupo" : "Crear Nuevo Grupo"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isEditing
            ? "Modifica los datos del grupo existente"
            : "Ingresa los datos del nuevo grupo"}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Error de envío */}
          {submitError && <Alert severity="error">{submitError}</Alert>}

          {/* Información del grupo del sistema */}
          {isEditing && group?.es_sistema && (
            <Alert severity="warning">
              <Typography variant="body2">
                <strong>Grupo del Sistema:</strong> Este grupo tiene
                restricciones especiales y algunas operaciones pueden estar
                limitadas.
              </Typography>
            </Alert>
          )}

          {/* Campo Nombre */}
          <TextField
            label="Nombre del Grupo"
            value={formData.nombre}
            onChange={handleInputChange("nombre")}
            onKeyPress={handleKeyPress}
            error={Boolean(errors.nombre)}
            helperText={
              errors.nombre || "Nombre único para identificar el grupo"
            }
            required
            size="small"
            fullWidth
            disabled={Boolean(
              isSubmitting || loading || (isEditing && group?.es_sistema),
            )}
            placeholder="Ej: Administradores, Operadores, etc."
          />

          {/* Campo Descripción */}
          <TextField
            label="Descripción"
            value={formData.descripcion}
            onChange={handleInputChange("descripcion")}
            error={Boolean(errors.descripcion)}
            helperText={
              errors.descripcion ||
              "Descripción opcional del grupo (máximo 500 caracteres)"
            }
            size="small"
            fullWidth
            multiline
            rows={3}
            disabled={isSubmitting || loading}
            placeholder="Describe el propósito y funciones de este grupo..."
          />

          {/* Campo Estado Activo */}
          <FormControlLabel
            control={
              <Switch
                checked={formData.activo}
                onChange={handleInputChange("activo")}
                disabled={Boolean(
                  isSubmitting || loading || (isEditing && group?.es_sistema),
                )}
                size="small"
              />
            }
            label={
              <Stack>
                <Typography variant="body2">Grupo Activo</Typography>
                <Typography variant="caption" color="text.secondary">
                  Solo los grupos activos pueden ser asignados a usuarios
                </Typography>
              </Stack>
            }
          />

          {/* Información adicional para edición */}
          {isEditing && group && (
            <Alert severity="info">
              <Stack spacing={1}>
                <Typography variant="body2">
                  <strong>Grupo creado:</strong>{" "}
                  {new Date(
                    group.fecha_creacion || Date.now(),
                  ).toLocaleDateString("es-ES")}
                </Typography>
                <Typography variant="body2">
                  <strong>Tipo:</strong>{" "}
                  {group.es_sistema ? "Sistema" : "Personalizado"}
                </Typography>
                <Typography variant="body2">
                  <strong>Estado actual:</strong>{" "}
                  {group.activo ? "Activo" : "Inactivo"}
                </Typography>
              </Stack>
            </Alert>
          )}

          {/* Información de ayuda para nuevo grupo */}
          {!isEditing && (
            <Alert severity="info">
              <Typography variant="body2">
                <strong>Consejos:</strong>
              </Typography>
              <Typography variant="body2">
                • Usa nombres descriptivos (ej: "Administradores", "Vendedores")
                <br />
                • Los grupos permiten asignar permisos de forma organizada
                <br />• Puedes crear grupos específicos por departamento o
                función
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={isSubmitting}
          size="small"
          startIcon={<CancelIcon />}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting || loading}
          variant="contained"
          size="small"
          startIcon={
            isSubmitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
        >
          {isSubmitting ? "Guardando..." : isEditing ? "Actualizar" : "Crear"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
